import { ITaxProfile } from '@smsamiinc/catalog';
import { ORG_TYPES, LEFT_NAVBAR_ITEMS, USER_ROLE } from 'src/app/app-constants';
import { OrgTypesModel } from 'src/app/auth/models/org-types.model';
import { Model, MapTo } from 'src/app/decorators/mapto';
import { memberIds } from 'src/app/interfaces/supportStaff';
import { environment } from 'src/environments/environment';

import { CountriesModel } from './countries.model';
import { MemberRoles, MembersModel } from './members.model';
import { SettingBaseDataModel } from './settings-base-data.model';
import { SettingBaseModel } from './settings-base.model';
import { StatesAndCitiesModel } from './states.model';
import { TaxModel } from './tax.model';

export class OrgModel extends Model {
    index: number;

    @MapTo()
    id: string;
    @MapTo()
    name: string;
    @MapTo()
    email: string;
    @MapTo()
    contact_number: string;
    @MapTo()
    address: string;
    @MapTo()
    city: StatesAndCitiesModel;
    @MapTo()
    state: StatesAndCitiesModel;
    @MapTo()
    country: CountriesModel;
    @MapTo()
    discount: string;
    @MapTo()
    memberId: string;
    @MapTo()
    image: string;
    @MapTo()
    is_discount: boolean;
    @MapTo()
    website: string;
    @MapTo()
    ntn: string;
    @MapTo('settings', [SettingBaseModel])
    settings: SettingBaseModel[];
    @MapTo()
    organization_type: OrgTypesModel;
    @MapTo()
    strn: number;
    @MapTo()
    kntn: string;
    @MapTo()
    token: string;
    @MapTo()
    user_id: string;
    @MapTo('taxes', [TaxModel])
    taxes: TaxModel[];
    @MapTo()
    roles: MemberRoles[] = [];
    @MapTo()
    is_online: number;
    @MapTo()
    table_prefix: string;
    @MapTo()
    cover_image: string;
    @MapTo()
    organization_show_to: string;
    @MapTo()
    taxProfileListing: ITaxProfile[];
    @MapTo()
    time_zone: string;
    constructor(payload: any) {
        super(payload);
        this.setValues(payload);
    }

    get IsPurchaseSettingBase(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit.toUpperCase() === 'purchase_order'.toUpperCase();
        });
        return !!se?.is_enable;
    }

    get IsTransectionLogSettingBase(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit.toUpperCase() === 'transaction_log'.toUpperCase();
        });
        if (se) {
            return !!se.is_enable;
        }
        return false;
    }

    get IsOnlineOrderSettingBase(): boolean {
        return !!this.is_online;
    }

    get IsTaxSettingBase(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit === 'tax';
        });
        return !!se?.is_enable;
    }

    get IsMenuOfItemsSettingBase(): boolean {
        return false;
    }

    get IsCommentsEnable(): boolean {
        const customerComments = this.settings.find((setting) => {
            return setting?.unit === 'order_comment';
        });
        if (customerComments) {
            return !!customerComments?.is_enable;
        }
        return false;
    }
    get IsTaxInclusivePrice(): boolean {
        const tax_inclusive_price = this.settings.find(
            (setting: SettingBaseModel) => setting?.unit === 'tax_inclusive_price'
        );
        if (tax_inclusive_price) {
            return !!tax_inclusive_price?.is_enable;
        }
        return false;
    }

    get IsKOTCommentsEnable(): boolean {
        const KOTComments = this.settings.find((setting) => {
            return setting?.unit === 'order_comment';
        });
        if (KOTComments) {
            return !!KOTComments?.data?.kot;
        }
        return false;
    }
    get IsReceiptCommentsEnable(): boolean {
        const receiptComments = this.settings.find((setting) => {
            return setting?.unit === 'order_comment';
        });
        if (receiptComments) {
            return !!receiptComments?.data?.receipt;
        }
        return false;
    }
    filterSetting(settingName: string): SettingBaseModel {
        return this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === settingName;
        });
    }
    get IsTaxReductionSettingBase(): boolean {
        const se = this.filterSetting('tax_reduction');
        return !!se?.is_enable;
    }
    get IsCreditcardTaxSettingBase(): number {
        if (this.IsTaxReductionSettingBase) {
            const se = this.filterSetting('tax_reduction');
            if (se?.data?.credit_card?.is_enable) {
                return se?.data?.credit_card?.value;
            }
            return 0;
        }
        return 0;
    }
    get IsEasyPaisaTaxSettingBase(): number {
        if (this.IsTaxReductionSettingBase) {
            const se = this.filterSetting('tax_reduction');
            if (se?.data?.easy_paisa?.is_enable) {
                return se?.data?.easy_paisa?.value;
            }
            return 0;
        }
        return 0;
    }
    get IsJazzCashTaxSettingBase(): number {
        if (this.IsTaxReductionSettingBase) {
            const se = this.filterSetting('tax_reduction');
            if (se?.data?.jazz_cash?.is_enable) {
                return se?.data?.jazz_cash?.value;
            }
            return 0;
        }
        return 0;
    }
    get IsBankTransferSettingBase(): number {
        if (this.IsTaxReductionSettingBase) {
            const se = this.filterSetting('tax_reduction');
            if (se?.data?.bank_transfer?.is_enable) {
                return se?.data?.bank_transfer?.value;
            }
            return 0;
        }
        return 0;
    }
    get IsCommittedOrderPaymentType(): boolean {
        const se = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'committed_order_payment_type';
        });
        return !!se?.is_enable;
    }
    get IsDiscountSettingBase(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit === 'discount';
        });
        return !!se?.is_enable;
    }
    get IsReceiptNoteSettingBase(): boolean {
        const note = this.settings.find((setting) => {
            return setting?.unit === 'note';
        });
        if (note) {
            return !!note?.is_enable;
        }
        return false;
    }
    get IsCurrencySettingBase(): string {
        const currency = this.settings.find((setting: SettingBaseModel) => {
            return setting.unit === 'currency';
        });
        if (currency?.is_enable) {
            if (currency?.data?.united_arab_emirates_dirham) {
                return 'AED';
            }
            if (currency?.data?.united_states_dollar) {
                return 'USD';
            }
        }
        return 'PKR';
    }

    get IsDiscountInPercentage(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit === 'discount';
        });
        return !!se?.data?.percentage;
    }
    get IsBrandingEnable(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'branding';
        });
        return !!setting?.is_enable;
    }
    get IsServiceChargesInPercentage(): boolean {
        const serviceCharges = this.settings.find((setting) => {
            return setting?.unit === 'service_charge';
        });
        if (serviceCharges) {
            return !!serviceCharges.data?.percentage;
        }
        return false;
    }
    get IsServiceChargesInAmount(): boolean {
        const serviceCharges = this.settings.find((setting) => {
            return setting?.unit === 'service_charge';
        });
        if (serviceCharges) {
            return !!serviceCharges.data?.amount;
        }
        return false;
    }
    get ServiceChargesRate(): number {
        const serviceCharges = this.settings.find((setting) => {
            return setting?.unit === 'service_charge';
        });
        if (serviceCharges) {
            return serviceCharges.data?.rate;
        }
        return 0;
    }
    get itemBasedTax(): number {
        const skip_tax_item = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'skip_tax_item';
        });
        return skip_tax_item.is_enable;
    }

    // Delivery Charges
    get IsDeliveryChargesInPercentage(): boolean {
        const deliveryCharges = this.settings.find((setting) => {
            return setting?.unit === 'delivery_charge';
        });
        if (deliveryCharges) {
            return !!deliveryCharges.data?.percentage;
        }
        return false;
    }
    get IsDeliveryChargesInAmount(): boolean {
        const deliveryCharges = this.settings.find((setting) => {
            return setting?.unit === 'delivery_charge';
        });
        if (deliveryCharges) {
            return !!deliveryCharges.data?.amount;
        }
        return false;
    }
    get DeliveryChargesRate(): number {
        const deliveryCharges = this.settings.find((setting) => {
            return setting?.unit === 'delivery_charge';
        });
        if (deliveryCharges) {
            return deliveryCharges.data?.rate;
        }
        return 0;
    }
    get IsDeliveryChargesFixed(): boolean {
        const deliveryCharges: SettingBaseModel = this.settings.find(
            (setting: SettingBaseModel) => setting?.unit === 'delivery_charge'
        );
        if (deliveryCharges) {
            return !!deliveryCharges.data?.fixed;
        }
        return false;
    }
    get Istrnlabel(): boolean {
        const setting = this.settings.find((setting) => {
            return setting?.unit === 'field_label';
        });
        if (setting?.is_enable) {
            return !!setting.data?.ntn?.isenable;
        }
        return false;
    }

    get IsvatTaxLabel(): boolean {
        const setting: SettingBaseModel = this.settings?.find((setting: SettingBaseModel) => {
            return setting?.unit === 'field_label';
        });
        if (setting?.is_enable) {
            return !!setting.data?.sts?.isenable;
        }
        return false;
    }

    get IsDiscountInAmount(): boolean {
        const se = this.settings.find((setting) => {
            return setting?.unit === 'discount';
        });
        return !!se?.data?.amount;
    }

    get IsServiceAreaEnable(): boolean {
        const serviceArea = this.settings.find((setting) => {
            return setting?.unit === 'service_area';
        });
        if (serviceArea) {
            return !!serviceArea.is_enable;
        }
        return false;
    }
    get IsBeepSoundEnable(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => setting?.unit === 'online_support');
        return setting ? !!setting.is_enable : false;
    }
    get IsPluSettingBased(): boolean {
        const scaleBase: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'scale';
        });
        if (scaleBase) {
            return !!scaleBase.is_enable;
        }
        return false;
    }
    get IsItemSort(): boolean {
        const itemSort = this.settings.find((setting) => {
            return setting?.unit === 'sort_item';
        });
        if (itemSort) {
            return !!itemSort.is_enable;
        }
        return false;
    }
    get individualCartItem(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => setting?.unit === 'individual_cart_item');
        return setting ? !!setting?.is_enable : false;
    }
    get SupportStaffData(): memberIds | [] {
        const setting = this.settings.find((setting: SettingBaseModel) => setting?.unit === 'online_support');
        return setting?.is_enable ? setting?.data?.ids : [];
    }
    get IsItemPriceSettingBase(): boolean {
        const item_price = this.settings.find((setting) => {
            return setting?.unit === 'kot_item_price';
        });
        if (item_price) {
            return !!item_price.is_enable;
        }
        return false;
    }

    get IsAdmin(): boolean {
        const isAdmin = this.roles.find((user) => {
            return user?.userrole_id === 1;
        });
        if (isAdmin) {
            return !!isAdmin;
        }
        return false;
    }
    get IsCashier(): boolean {
        const isCashier = this.roles.find((user) => {
            return user.userrole_id === 2;
        });
        if (isCashier) {
            return !!isCashier;
        }
        return false;
    }

    get IsManager(): boolean {
        const manager = this.roles.filter((role) => {
            return role.userrole_id === 3;
        });

        return !!manager.length;
    }

    get IsWaiter(): boolean {
        const isWaiter = this.roles.find((user) => {
            return user.userrole_id === 4;
        });
        if (isWaiter) {
            return !!isWaiter;
        }
        return false;
    }

    get IsSubTotalSettingBase(): boolean {
        const subTotal = this.settings.find((setting) => {
            return setting?.unit === 'kot_sub_total';
        });
        if (subTotal) {
            return !!subTotal.is_enable;
        }
        return false;
    }

    get IsServiceChargesEnable(): boolean {
        const serviceCharges = this.settings.find((setting) => {
            return setting?.unit === 'service_charge';
        });
        if (serviceCharges) {
            return !!serviceCharges.is_enable;
        }
        return false;
    }

    get IsDeliveryChargesEnable(): boolean {
        const deliveryCharges = this.settings.find((setting) => {
            return setting.unit === 'delivery_charge';
        });
        if (deliveryCharges) {
            return !!deliveryCharges.is_enable;
        }
        return false;
    }

    get IsTableNumberSettingBase(): boolean {
        const tableNumber = this.settings.find((setting) => {
            return setting?.unit === 'table_number';
        });
        if (tableNumber) {
            return !!tableNumber.is_enable;
        }
    }

    get IsKotButtonSettingBase(): boolean {
        const IsKot = this.settings.find((setting) => {
            return setting?.unit === 'kot_button';
        });
        if (IsKot) {
            return !!IsKot.is_enable;
        }
        return false;
    }

    get IsKotButtonInListSettingBase(): boolean {
        const IsKot = this.settings.find((setting) => {
            return setting?.unit === 'kot_in_list';
        });
        if (IsKot) {
            return !!IsKot.is_enable;
        }
        return false;
    }

    get IsItemTaxSettingBase(): boolean {
        const itemTaxSetting: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
            return setting.unit === 'item_tax_include';
        });
        if (itemTaxSetting) {
            return !!itemTaxSetting.is_enable;
        }
        return false;
    }

    get IsSaveReceiptSettingBase(): boolean {
        const saveReceipt = this.settings.find((setting) => {
            return setting?.unit === 'receipt_on_save';
        });
        if (saveReceipt) {
            return !!saveReceipt.is_enable;
        }
        return false;
    }

    get ScaleBasedSetting(): boolean {
        const scaleBased: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'scale';
        });

        if (scaleBased) {
            return !!scaleBased?.is_enable;
        }
        return false;
    }
    get PendingOrderSettingsBased(): boolean {
        const pendingLogs: SettingBaseModel = this.settings.find((setting) => {
            return setting?.unit === 'pending_logs';
        });
        if (pendingLogs) {
            if (this.RoleId === USER_ROLE.waiter.id) {
                if (pendingLogs?.data?.waiter?.isenable) {
                    return !!pendingLogs?.is_enable;
                }
                return false;
            }
            return true;
        }
    }

    get PendingLogEditRestrictionsSettingsBased(): boolean {
        if (this.RoleId === USER_ROLE.admin.id) {
            return false;
        }
        const pendingLogs: SettingBaseModel = this.settings.find((setting) => {
            return setting?.unit === 'pending_logs';
        });
        if (pendingLogs) {
            if (this.RoleId === USER_ROLE.waiter.id) {
                return pendingLogs?.data?.waiter?.edit_restriction;
            }
            if (this.RoleId === USER_ROLE.operator.id) {
                return pendingLogs?.data?.cashier?.edit_restriction;
            }
        }
    }

    get IsMultiplePaymentReceipts(): boolean {
        const isMultiple = this.settings.find((setting) => {
            return setting?.unit === 'multiple_payment_receipts';
        });
        if (isMultiple) {
            return !!isMultiple.is_enable;
        }
        return false;
    }
    get IsPaymentTypeSettingBase(): string[] {
        const paymentType: string[] = [];
        const payment: SettingBaseModel = this.settings.find(
            (setting: SettingBaseModel) => setting?.unit === 'payment_types'
        );
        if (payment.is_enable) {
            const paymentData: SettingBaseDataModel = payment.data;
            let copyKey = '';
            for (const key in paymentData) {
                if (paymentData[key]) {
                    if (key.includes('_')) {
                        copyKey = key
                            .split('_')
                            .map((key) => key.charAt(0).toUpperCase() + key.substring(1))
                            .join(' ');
                    } else {
                        copyKey = key.charAt(0).toUpperCase() + key.substring(1);
                    }
                    paymentType.push(copyKey);
                }
            }
        }
        return paymentType;
    }
    get IsDineInOrderTypeSettingBase(): boolean {
        const dineIn = this.settings.find((setting) => {
            return setting?.unit === 'order_types';
        });
        return !!dineIn?.data?.dine_in;
    }

    get IsDeliveryOrderTypeSettingBase(): boolean {
        const delivery = this.settings.find((setting) => {
            return setting?.unit === 'order_types';
        });
        return !!delivery?.data?.delivery;
    }
    get IsSaleReportSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isSaleReport: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'sale_report'.toUpperCase();
            });
            if (isSaleReport.is_enable) {
                if (this.RoleId === USER_ROLE.admin.id) {
                    return isSaleReport?.data?.admin;
                }
                if (this.RoleId === USER_ROLE.manager.id) {
                    return isSaleReport?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id && typeof isSaleReport?.data?.cashier === 'boolean') {
                    return isSaleReport.data.cashier as boolean;
                }
            }
        }
        return false;
    }

    get IsItemWiseReportSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isItemReport: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'item_report'.toUpperCase();
            });
            if (isItemReport.is_enable) {
                if (this.RoleId === USER_ROLE.admin.id) {
                    return isItemReport?.data?.admin;
                }
                if (this.RoleId === USER_ROLE.manager.id) {
                    return isItemReport?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id && typeof isItemReport?.data?.cashier === 'boolean') {
                    return isItemReport.data.cashier as boolean;
                }
            }
        }
        return false;
    }
    get IsCategoryWiseReportSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isCategoryReport: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'category_report'.toUpperCase();
            });
            if (isCategoryReport.is_enable) {
                if (this.RoleId === USER_ROLE.admin.id) {
                    return isCategoryReport?.data?.admin;
                }
                if (this.RoleId === USER_ROLE.manager.id) {
                    return isCategoryReport?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id && typeof isCategoryReport?.data?.cashier === 'boolean') {
                    return isCategoryReport.data.cashier as boolean;
                }
            }
        }
        return false;
    }
    get IsProfitLossReportSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isProfitLossReport: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'profit_loss_report'.toUpperCase();
            });
            if (isProfitLossReport.is_enable) {
                if (this.RoleId === USER_ROLE.admin.id) {
                    return isProfitLossReport?.data?.admin;
                }
                if (this.RoleId === USER_ROLE.manager.id) {
                    return isProfitLossReport?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id && typeof isProfitLossReport?.data?.cashier === 'boolean') {
                    return isProfitLossReport.data.cashier as boolean;
                }
            }
        }
        return false;
    }

    get IsDisableAllReportSettingBased(): boolean {
        const reports: SettingBaseModel = this.settings.find(
            (setting: SettingBaseModel) => setting?.unit === 'disable_all_reports'
        );
        if (reports) {
            return !!reports.is_enable;
        }
        return false;
    }

    get IsTakeAwayOrderTypeSettingBase(): boolean {
        const takeAway = this.settings.find((setting) => {
            return setting?.unit === 'order_types';
        });
        return !!takeAway?.data?.take_away;
    }

    get IsFoodPandaOrderTypeSettingBase(): boolean {
        const foodpanda = this.settings.find((setting) => {
            return setting?.unit === 'order_types';
        });
        return !!foodpanda?.data?.food_panda;
    }
    get IsJoviOrderTypeSettingBase(): boolean {
        const Jovi: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'order_types';
        });
        return !!Jovi?.data?.jovi;
    }

    get OrderTypeShowInReceipt(): boolean {
        const IsShowInReceipt = this.settings.find((setting) => {
            return setting?.unit === 'order_types';
        });
        return !!IsShowInReceipt?.data?.type_in_receipt;
    }

    get IsSaveButtonSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isSave: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'save_btn'.toUpperCase();
            });
            if (isSave) {
                return !!isSave.is_enable;
            }
        }
        return false;
    }

    get IsTableNumberRequired(): boolean {
        const tableNumber = this.settings.find((setting) => {
            return setting?.unit === 'table_number';
        });
        return !!tableNumber?.data?.required;
    }
    //TODO: these both business hour settings are commented for now as rtm is not operational for now
    // get RtmBusinessHours(): SettingBaseDataModel {
    //     const rtmBusinessHours = this.settings.find((setting) => {
    //         return setting?.unit === 'rtm_business_hours';
    //     });
    //     return rtmBusinessHours?.data || ({} as SettingBaseDataModel);
    // }

    // get BussinessHours(): { startTime: string; endTime: string } {
    //     const rtmTiming: SettingBaseDataModel = this.RtmBusinessHours;
    //     const startendtime = { startTime: '', endTime: '' };
    //     startendtime.startTime = this.TimeFormating(rtmTiming.starting_time);
    //     startendtime.endTime = this.TimeFormating(rtmTiming.ending_time);
    //     return startendtime || { startTime: '', endTime: '' };
    // }
    //TODO: ignore any for now
    get orgBusinessHours(): SettingBaseDataModel | any {
        const orgSetting = this.settings.find((setting) => {
            return setting?.unit === 'business_hours';
        });
        if (orgSetting?.is_enable) {
            return orgSetting?.data;
        } else {
            return false;
        }
    }

    get BussinessHours(): { startTime: string; endTime: string } {
        if (this.orgBusinessHours) {
            //TODO:Ignore any for now
            const orgTiming: any = this.orgBusinessHours;
            const startendtime = { startTime: '', endTime: '' };
            startendtime.startTime = orgTiming.starting_time;
            startendtime.endTime = orgTiming.ending_time;
            return startendtime;
        } else {
            return { startTime: '', endTime: '' };
        }
    }
    //TODO:have commented this code to avoid 12 hour format
    // TimeFormating(startendtime: string): string {
    //     let timeParts: Array<string> = [];
    //     timeParts = startendtime.split(':');
    //     if (+timeParts[0] < 12 || timeParts[0] === '00') {
    //         if (timeParts[0] === '00') {
    //             timeParts[0] = '12';
    //         }
    //         timeParts[1] = timeParts[1].concat('AM');
    //     }
    //     if (+timeParts[0] >= 12 && timeParts[0] !== '00' && !timeParts[1].includes('AM')) {
    //         if (+timeParts[0] !== 12) {
    //             timeParts[0] = (+timeParts[0] - 12).toString();
    //         }
    //         timeParts[1] = timeParts[1].concat('PM');
    //     }
    //     return timeParts.join(':');
    // }

    get IsCalculatorSettingBase(): boolean {
        if (Array.isArray(this.settings)) {
            const isShowInReceipt = this.settings.find((setting) => {
                return setting?.unit?.toUpperCase() === 'payment_calculator'.toUpperCase();
            });
            if (isShowInReceipt) {
                return !!isShowInReceipt.is_enable;
            }
        }
        return false;
    }

    get TLogReportsSettingBase(): boolean {
        if (this.RoleId === USER_ROLE.admin.id) {
            return true;
        }
        if (Array.isArray(this.settings)) {
            const IsShowCalulation = this.settings.find((setting) => {
                return setting?.unit?.toUpperCase() === 'transaction_log_report'.toUpperCase();
            });
            if (IsShowCalulation?.is_enable) {
                if (this.RoleId === USER_ROLE.manager.id) {
                    return IsShowCalulation?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id) {
                    if (typeof IsShowCalulation?.data?.cashier === 'boolean') {
                        return IsShowCalulation?.data?.cashier as boolean;
                    } else {
                        return IsShowCalulation?.data?.cashier?.isenable;
                    }
                }
            }
            return false;
        }
        return false;
    }

    get transactionLogDeleteSettingBase(): boolean {
        if (this.RoleId === USER_ROLE.admin.id) {
            return true;
        }
        if (Array.isArray(this.settings)) {
            const IsDeleteButton: SettingBaseModel = this.settings.find((setting: SettingBaseModel) => {
                return setting?.unit?.toUpperCase() === 'transaction_log_delete_button'.toUpperCase();
            });
            if (IsDeleteButton?.is_enable) {
                if (this.RoleId === USER_ROLE.manager.id) {
                    return IsDeleteButton?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id) {
                    if (typeof IsDeleteButton?.data?.cashier === 'boolean') {
                        return IsDeleteButton?.data?.cashier as boolean;
                    }
                }
            }
            return false;
        }

        return false;
    }

    get CancelOrderSettingBased(): boolean {
        if (this.RoleId === USER_ROLE.admin.id) {
            return true;
        }
        if (Array.isArray(this.settings)) {
            const isShowCancel = this.settings.find((setting) => {
                return setting?.unit?.toUpperCase() === 'cancel_order_btn'.toUpperCase();
            });
            if (isShowCancel?.is_enable) {
                if (this.RoleId === USER_ROLE.manager.id) {
                    return isShowCancel?.data?.manager;
                }
                if (this.RoleId === USER_ROLE.operator.id) {
                    return isShowCancel?.data?.cashier?.isenable;
                }
            }
            return false;
        }
        return false;
    }

    get ApplayTax() {
        let taxRate;
        this.taxes.map((tax) => {
            if (tax) {
                taxRate = tax.rate;
            }
        });
        return taxRate || 0;
    }

    get Token() {
        return this.token || 'N/A';
    }
    get userId() {
        return this.user_id || 'N/A';
    }

    get UserAddress() {
        return this.address || 'N/A';
    }

    get UserName() {
        return this.name || 'N/A';
    }

    get UserContactNumber() {
        return this.contact_number || 'N/A';
    }

    get CityId() {
        return this.city ? this.city.id : null;
    }

    get StateId() {
        return this.state ? this.state.state_id : null;
    }

    get orgTypeId() {
        return this.organization_type.id;
    }

    get IsStore() {
        return this.organization_type.id === ORG_TYPES.store.id;
    }

    get isWholeSale() {
        return this.organization_type.id === ORG_TYPES.wholeSale.id;
    }

    get IsRestaurant() {
        return this.organization_type.id === ORG_TYPES.restaurant.id;
    }
    get IsPharmacy() {
        return this.organization_type.id === ORG_TYPES.pharmacy.id;
    }
    get AccountsSideMenu(): object {
        return this.accountsMenu();
    }

    get PosSideMenu() {
        // TO DO Refactor this to make it generic
        if (this.IsRestaurant) {
            if (
                !this.IsSaleReportSettingBase &&
                !this.IsItemWiseReportSettingBase &&
                !this.IsCategoryWiseReportSettingBase &&
                !this.IsProfitLossReportSettingBase
            ) {
                this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
                    if (menu.title.toUpperCase() === 'REPORTS') {
                        menu.canSeeByRole.splice(0);
                    }
                });
            }
            if (!this.IsSaleReportSettingBase) {
                this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
                    if (Array.isArray(menu.children)) {
                        this.index = menu.children.findIndex(
                            (child) => child.title.toUpperCase() === 'Sales'.toUpperCase()
                        );
                        if (menu?.children[this.index]?.title?.toUpperCase() === 'Sales'.toUpperCase()) {
                            menu.children.splice(this.index, 1);
                        }
                    }
                });
            }

            if (!this.IsItemWiseReportSettingBase) {
                this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
                    if (Array.isArray(menu.children)) {
                        this.index = menu.children.findIndex(
                            (child) => child.title.toUpperCase() === 'Item Wise Sales'.toUpperCase()
                        );
                        if (menu?.children[this.index]?.title?.toUpperCase() === 'Item Wise Sales'.toUpperCase()) {
                            menu.children.splice(this.index, 1);
                        }
                    }
                });
            }
            //TODO:Have commented these two reports
            // if (!this.IsCategoryWiseReportSettingBase) {
            //     this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
            //         if (Array.isArray(menu.children)) {
            //             this.index = menu.children.findIndex(
            //                 (child) =>
            //                     child.title.toUpperCase() === 'Category Wise Sales'.toUpperCase()
            //             );
            //             menu.children.splice(this.index, 1);
            //         }
            //     });
            // }
            // if (!this.IsProfitLossReportSettingBase) {
            //     this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
            //         if (Array.isArray(menu.children)) {
            //             this.index = menu.children.findIndex(
            //                 (child) => child.title.toUpperCase() === 'Profit/Loss'.toUpperCase()
            //             );
            //             menu.children.splice(this.index, 1);
            //         }
            //     });
            // }
            if (!this.IsTransectionLogSettingBase) {
                this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
                    if (menu.title.toUpperCase() === 'Transaction logs'.toUpperCase()) {
                        menu.canSeeByRole.splice(1);
                    }
                });
            }
            if (this.IsDisableAllReportSettingBased) {
                this.posMenu(ORG_TYPES.restaurant.id).filter((menu) => {
                    if (menu.title.toUpperCase() === 'REPORTS') {
                        menu.canSeeByRole.splice(0);
                    }
                });
            }
            return this.posMenu(ORG_TYPES.restaurant.id);
        }
        if (this.isWholeSale) {
            return this.posMenu(ORG_TYPES.wholeSale.id);
        }
        if (this.IsStore) {
            return this.posMenu(ORG_TYPES.store.id);
        }

        if (this.IsPharmacy) {
            return this.posMenu(ORG_TYPES.pharmacy.id);
        }

        return LEFT_NAVBAR_ITEMS.pos;
    }

    get ImageUrl() {
        return this.image ? this.image : environment.defualtThumbnail;
    }

    get CoverImageUrl(): string {
        return this.cover_image ? this.cover_image : environment.defualtThumbnail;
    }

    get InventorySideMenu() {
        if (this.IsRestaurant) {
            return this.inventoryMenu(ORG_TYPES.restaurant.id);
        }
        if (this.IsStore) {
            return this.inventoryMenu(ORG_TYPES.store.id);
        }
        if (this.isWholeSale) {
            return this.inventoryMenu(ORG_TYPES.wholeSale.id);
        }

        if (this.IsPharmacy) {
            return this.inventoryMenu(ORG_TYPES.pharmacy.id);
        }
    }
    get MemberSideMenu() {
        if (this.IsRestaurant) {
            return this.memberMenu(ORG_TYPES.restaurant.id);
        }
        if (this.IsStore) {
            return this.memberMenu(ORG_TYPES.store.id);
        }
        if (this.isWholeSale) {
            return this.memberMenu(ORG_TYPES.wholeSale.id);
        }
        if (this.IsPharmacy) {
            return this.memberMenu(ORG_TYPES.pharmacy.id);
        }
    }

    get OrgAddress(): string {
        return this.address || 'N/A';
    }

    get OrgCity(): string {
        if (this.city) {
            return this.city.name;
        }
        return '';
    }

    get OrgState(): string {
        if (this.state) {
            return this.state.name;
        }
        return '';
    }

    get OrgCountry(): string {
        if (this.country) {
            return this.country.name;
        }
        return '';
    }

    get OrgNTN(): string {
        return this.ntn || null;
    }
    get OrgKNTN(): string {
        return this.kntn || null;
    }

    get OrgSTRN(): number {
        return this.strn || null;
    }

    get OrgName(): string {
        return this.name || 'N/A';
    }

    get OrgWebsite(): string {
        return this.website || 'N/A';
    }

    get OrgEmail(): string {
        return this.email || 'N/A';
    }

    get IsDiscountEnableToStaff(): boolean {
        return !!this.discount;
    }

    get TablePrefix(): string {
        if (this.table_prefix) {
            return this.table_prefix + ' ';
        }
        return '';
    }

    posMenu(orgId: number) {
        return LEFT_NAVBAR_ITEMS.pos.filter((menu) => {
            if (menu.canSee) {
                return menu.canSee.find((id) => id === orgId);
            }
            return true;
        });
    }

    inventoryMenu(orgId: number) {
        return LEFT_NAVBAR_ITEMS.inventory.filter((menu) => {
            if (orgId === ORG_TYPES.pharmacy.id && menu.canSee) {
                return menu.canSee.find((id: number) => id === orgId);
            }
            // TODO should not compare magic string like what if purchase spell change in left nav so this going to be fail
            if (!this.IsPurchaseSettingBase) {
                return menu.title !== 'Purchase' && menu.title !== 'Distributors';
            }
            if (Array.isArray(this.roles) && menu.canSeeByRole?.length) {
                return menu.canSeeByRole.find((id) => id === this.roles[0]?.userrole_id);
            }
            if (menu.canSee) {
                return menu.canSee.find((id) => id === orgId);
            }
            return true;
        });
    }

    memberMenu(orgId: number) {
        return LEFT_NAVBAR_ITEMS.members.filter((menu) => {
            if (Array.isArray(this.roles) && menu.canSeeByRole?.length) {
                return menu.canSeeByRole.find((id) => id === this.roles[0]?.userrole_id);
            }
            if (menu.canSee) {
                return menu.canSee.find((id) => id === orgId);
            }
            return true;
        });
    }

    accountsMenu(): object {
        return LEFT_NAVBAR_ITEMS.accounts.filter((menu) => {
            if (Array.isArray(this.roles) && menu.canSeeByRole?.length) {
                menu.canSeeByRole.find((id: number) => id === this.roles[0]?.userrole_id);
            }
            return true;
        });
    }

    get OrgContactNumber(): string {
        return this.contact_number || 'N/A';
    }

    get RoleId(): number {
        let userrole_id: number;
        this.roles.filter((role) => {
            userrole_id = role.userrole_id;
        });
        return userrole_id;
    }
    // TODO: Will optimize the below receipt_format settings
    get IsReceiptFormatSettingBase(): boolean {
        const se = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'receipt_format';
        });
        return !!se?.is_enable;
    }

    get IsShowTaxTypeWithItemName(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'receipt_format';
        });
        return !!setting?.data?.show_sts_percent_with_item_name;
    }

    get IsShowTaxTypeWithTotalTax(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'receipt_format';
        });
        return !!setting?.data?.show_sts_percent_with_total_tax;
    }
    get IsShowTaxColumn(): boolean {
        const setting = this.settings.find((setting: SettingBaseModel) => {
            return setting?.unit === 'receipt_format';
        });
        return !!setting?.data?.show_tax_column;
    }
}

export class UserModel extends Model {
    @MapTo()
    display: string;
    @MapTo()
    email: string;
    @MapTo()
    contact_number: string;
    @MapTo()
    staff: MembersModel;

    constructor(payload: any) {
        super(payload);
        this.setValues(payload);
    }
}

export class MemberModel extends Model {
    @MapTo()
    id: number;
    @MapTo()
    user: UserModel;
    @MapTo()
    created_at: number;

    constructor(payload: any) {
        super(payload);
        this.setValues(payload);
    }

    get IsDiscountEnableToStaff(): boolean {
        return !!(this.user && this.user.staff && this.user.staff.rate);
    }

    get UserEmail() {
        return (this.user && this.user.email) || 'N/A';
    }

    get UserContactNumber() {
        return (this.user && this.user.contact_number) || 'N/A';
    }
}
